import { Component, computed, input, InputSignal, OnInit, output, OutputEmitterRef, signal, Signal, WritableSignal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import QRCode from 'qrcode';
import { catchError, concatMap, finalize, from, Observable, of, takeUntil, tap } from 'rxjs';
import {
    AutoFocusDirective,
    BaseComponent,
    ErrorsUtil,
    InputComponent,
    MessageBoxComponent,
    MessageContent,
    WaitButtonComponent,
    WaitComponent
} from '@sircon/sircon-ui-framework';
import { LoginHttpService } from 'src/providers/login-http.service';

@Component({
    selector: 'app-mfa-authenticator-setup',
    templateUrl: './mfa-authenticator-setup.component.html',
    styleUrls: ['./mfa-authenticator-setup.component.less'],
    imports: [ReactiveFormsModule, MessageBoxComponent, InputComponent, WaitButtonComponent, WaitComponent, AutoFocusDirective],
    standalone: true
})
export class MfaAuthenticatorSetupComponent extends BaseComponent implements OnInit {
    /**
     * Voluntary Account Settings flow shows Cancel (default). The mandatory login-time carrier-required
     * enrollment flow hides it - there is no skip/cancel option once a connected carrier requires MFA.
     */
    showCancelButton: InputSignal<boolean> = input(true);

    verified: OutputEmitterRef<void> = output<void>();
    cancelled: OutputEmitterRef<void> = output<void>();

    verificationForm: FormGroup;

    errors: MessageContent;
    qrCodeDataUrl: WritableSignal<string> = signal('');
    setupKey: WritableSignal<string> = signal('');
    setupLoading: WritableSignal<boolean> = signal(false);
    verifyingCode: WritableSignal<boolean> = signal(false);

    hasQrCode: Signal<boolean> = computed(() => !!this.qrCodeDataUrl());
    verificationStepReady: Signal<boolean> = computed(() => !!this.setupKey());

    constructor(
        private formBuilder: FormBuilder,
        private loginHttpService: LoginHttpService
    ) {
        super();
    }

    ngOnInit(): void {
        this.errors = ErrorsUtil.create();

        this.verificationForm = this.formBuilder.group({
            verificationCode: ['', Validators.compose([
                Validators.required,
                Validators.minLength(6),
                Validators.maxLength(6),
                Validators.pattern(/^[0-9]+$/)
            ])]
        });

        this.loadAuthenticatorSetup();
    }

    loadAuthenticatorSetup(): void {
        this.setupLoading.set(true);
        ErrorsUtil.clear(this.errors);

        this.loginHttpService.enrollAuthenticator()
            .pipe(
                tap((response) => {
                    this.setupKey.set(response.secretCode);
                }),
                concatMap((response) => this.generateQRCode$(response.secretCode)),
                catchError((error) => {
                    this.errors = ErrorsUtil.mapResponse(error);
                    return of();
                }),
                finalize(() => this.setupLoading.set(false)),
                takeUntil(this.ngUnsubscribe)
            )
            .subscribe();
    }

    generateQRCode$(secretCode: string): Observable<string> {
        const issuer = 'Sircon';
        const encodedIssuer = encodeURIComponent(issuer);
        const otpauthUrl = `otpauth://totp/${encodedIssuer}:user?secret=${secretCode}&issuer=${encodedIssuer}`;

        return from(QRCode.toDataURL(otpauthUrl))
            .pipe(
                tap((dataUrl) => {
                    this.qrCodeDataUrl.set(dataUrl);
                }),
                catchError(() => {
                    this.errors.globalErrors = ErrorsUtil.buildMessage('Failed to generate QR code. Please use the manual setup key below.');
                    return of();
                })
            );
    }

    verifyCode(): void {
        if (!this.verificationForm.valid) {
            return;
        }

        const code = this.verificationForm.value.verificationCode;
        this.verifyingCode.set(true);
        ErrorsUtil.clear(this.errors);

        this.loginHttpService.verifyAuthFactor({ value: code })
            .pipe(
                tap(() => {
                    this.verified.emit();
                }),
                catchError((error) => {
                    this.errors = ErrorsUtil.mapResponse(error);
                    // Only re-enable the field on failure. On success this component stays mounted
                    // briefly while the parent finishes loading context and navigating away - leaving
                    // verifyingCode true keeps the code field disabled instead of flashing editable again.
                    this.verifyingCode.set(false);
                    return of();
                }),
                takeUntil(this.ngUnsubscribe)
            )
            .subscribe();
    }
}
